import { createSimpleContext } from "@openanalyst-ai/ui/context"
import { type Accessor, batch, createEffect, createMemo, onCleanup } from "solid-js"
import { createStore, type SetStoreFunction, type Store } from "solid-js/store"
import { Persist, persisted } from "@/utils/persist"
import { useCheckServerHealth } from "@/utils/server-health"
import { ServerScope } from "@/utils/server-scope"

type StoredProject = { worktree: string; expanded: boolean }
type StoredServerConnection = string | ServerConnection.HttpBase | ServerConnection.Http
type ServerProjectState = { projects: Record<string, StoredProject[]>; lastProject: Record<string, string> }
const HEALTH_POLL_INTERVAL_MS = 10_000

export function normalizeServerUrl(input: string) {
  const trimmed = input.trim()
  if (!trimmed) return
  const withProtocol = /^https?:\/\//.test(trimmed) ? trimmed : `http://${trimmed}`
  return withProtocol.replace(/\/+$/, "")
}

export function serverName(conn?: ServerConnection.Any, ignoreDisplayName = false) {
  if (!conn) return ""
  if (conn.displayName && !ignoreDisplayName) return conn.displayName
  return conn.http.url.replace(/^https?:\/\//, "").replace(/\/+$/, "")
}

function isLocalHost(url: string) {
  const host = url.replace(/^https?:\/\//, "").split(":")[0]
  if (host === "localhost" || host === "127.0.0.1") return "local"
}

export function createServerProjects<T extends ServerProjectState>(input: {
  scope: Accessor<ServerScope>
  store: Store<T>
  setStore: SetStoreFunction<T>
}) {
  const setStore = input.setStore as unknown as SetStoreFunction<ServerProjectState>
  const current = () => input.store.projects[input.scope()] ?? []
  return {
    list: current,
    open(directory: string) {
      const scope = input.scope()
      if (!scope) return
      if (current().some((project) => project.worktree === directory)) return
      setStore("projects", scope, [{ worktree: directory, expanded: true }, ...current()])
    },
    close(directory: string) {
      setStore(
        "projects",
        input.scope(),
        current().filter((project) => project.worktree !== directory),
      )
    },
    expand(directory: string) {
      const index = current().findIndex((project) => project.worktree === directory)
      if (index !== -1) setStore("projects", input.scope(), index, "expanded", true)
    },
    collapse(directory: string) {
      const index = current().findIndex((project) => project.worktree === directory)
      if (index !== -1) setStore("projects", input.scope(), index, "expanded", false)
    },
    move(directory: string, toIndex: number) {
      const fromIndex = current().findIndex((project) => project.worktree === directory)
      if (fromIndex === -1 || fromIndex === toIndex) return
      const result = [...current()]
      const [item] = result.splice(fromIndex, 1)
      result.splice(toIndex, 0, item)
      setStore("projects", input.scope(), result)
    },
    last() {
      return input.store.lastProject[input.scope()]
    },
    touch(directory: string) {
      setStore("lastProject", input.scope(), directory)
    },
  }
}

export namespace ServerConnection {
  type Base = { displayName?: string; label?: string }

  export type HttpBase = {
    url: string
    username?: string
    password?: string
  }

  // Regular web connections
  export type Http = {
    type: "http"
    http: HttpBase
    authToken?: boolean
  } & Base

  export type Sidecar = {
    type: "sidecar"
    http: HttpBase
  } & (
    | // Regular desktop server
    { variant: "base" }
    // WSL server (windows only)
    | {
        variant: "wsl"
        distro: string
      }
  ) &
    Base

  // Remote server desktop can SSH into
  export type Ssh = {
    type: "ssh"
    host: string
    // SSH client exposes an HTTP server for the app to use as a proxy
    http: HttpBase
  } & Base

  export type Any =
    | Http
    // All these are desktop-only
    | (Sidecar | Ssh)

  export const key = (conn: Any): Key => {
    switch (conn.type) {
      case "http":
        return Key.make(conn.http.url)
      case "sidecar": {
        if (conn.variant === "wsl") return Key.make(`wsl:${conn.distro}`)
        return Key.make("sidecar")
      }
      case "ssh":
        return Key.make(`ssh:${conn.host}`)
    }
  }

  export type Key = string & { _brand: "Key" }
  export const Key = { make: (v: string) => v as Key }

  export const builtin = (conn: Any) => conn.type === "sidecar" && conn.variant === "base"
  export const local = (conn?: Any) =>
    !!conn && (builtin(conn) || (conn.type === "http" && isLocalHost(conn.http.url) === "local"))
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function isHttpBase(value: unknown): value is ServerConnection.HttpBase {
  return isRecord(value) && typeof value.url === "string" && value.url.length > 0
}

function storedServerUrl(value: StoredServerConnection) {
  return typeof value === "string" ? value : "type" in value ? value.http.url : value.url
}

function serverDisplayName(value: Record<string, unknown>) {
  return typeof value.displayName === "string" ? { displayName: value.displayName } : {}
}

function toServerConnection(value: unknown): ServerConnection.Any | undefined {
  if (typeof value === "string") return value ? { type: "http", http: { url: value } } : undefined
  if (!isRecord(value)) return undefined
  if (!("type" in value)) return isHttpBase(value) ? { type: "http", http: value } : undefined
  if (value.type === "http" && isHttpBase(value.http))
    return { type: "http", http: value.http, ...serverDisplayName(value) }
  if (value.type === "sidecar" && isHttpBase(value.http) && value.variant === "base")
    return { type: "sidecar", variant: "base", http: value.http, ...serverDisplayName(value) }
  if (value.type === "sidecar" && isHttpBase(value.http) && value.variant === "wsl" && typeof value.distro === "string")
    return {
      type: "sidecar",
      variant: "wsl",
      distro: value.distro,
      http: value.http,
      ...serverDisplayName(value),
    }
  if (value.type === "ssh" && typeof value.host === "string" && isHttpBase(value.http))
    return { type: "ssh", host: value.host, http: value.http, ...serverDisplayName(value) }
  return undefined
}

function isStoredServerConnection(value: unknown): value is StoredServerConnection {
  if (typeof value === "string") return value.length > 0
  if (!isRecord(value)) return false
  if (!("type" in value)) return isHttpBase(value)
  return value.type === "http" && isHttpBase(value.http)
}

function connectionHttpUrl(value: ServerConnection.Any): string | undefined {
  return value.http?.url
}

export function resolveServerConnections(input: { provided?: Array<unknown> | null; stored?: Array<unknown> | null }) {
  const provided = (Array.isArray(input.provided) ? input.provided : [])
    .map(toServerConnection)
    .filter((value): value is ServerConnection.Any => !!value)
  const stored = (Array.isArray(input.stored) ? input.stored : [])
    .map(toServerConnection)
    .filter((value): value is ServerConnection.Any => !!value)

  // The IPC-provided sidecar is the source of truth for credentials —
  // its password is regenerated each launch. Any stored HTTP entry that
  // points at the same URL is necessarily stale (it has yesterday's
  // password). Drop those so that resolveCurrentServer never picks the
  // stale stored entry over the fresh sidecar, even when defaultServerUrl
  // is persisted to that http://localhost:<port> URL.
  const providedSidecarUrls = new Set(
    provided.filter((value) => value.type === "sidecar").map(connectionHttpUrl).filter((url): url is string => !!url),
  )
  const filteredStored = stored.filter((value) => {
    if (value.type !== "http") return true
    const url = connectionHttpUrl(value)
    return !url || !providedSidecarUrls.has(url)
  })

  return [
    ...new Map(
      [...provided, ...filteredStored].map((value) => [ServerConnection.key(value), value]),
    ).values(),
  ]
}

export function resolveCurrentServer(servers: Array<ServerConnection.Any> | undefined, active: ServerConnection.Key) {
  const list = servers ?? []
  return list.find((server) => ServerConnection.key(server) === active) ?? list[0]
}

export const { use: useServer, provider: ServerProvider } = createSimpleContext({
  name: "Server",
  init: (props: {
    defaultServer: ServerConnection.Key
    disableHealthCheck?: boolean
    servers?: Array<ServerConnection.Any>
  }) => {
    const checkServerHealth = useCheckServerHealth()

    const [store, setStore, _, ready] = persisted(
      Persist.global("server", ["server.v3"]),
      createStore({
        list: [] as StoredServerConnection[],
        projects: {} as Record<string, StoredProject[]>,
        lastProject: {} as Record<string, string>,
      }),
    )

    const storedList = () => (Array.isArray(store.list) ? store.list.filter(isStoredServerConnection) : [])

    const allServers = createMemo((): Array<ServerConnection.Any> => {
      return resolveServerConnections({ provided: props.servers, stored: storedList() })
    })

    const [state, setState] = createStore({
      active: props.defaultServer,
      healthy: undefined as boolean | undefined,
    })

    const healthy = () => state.healthy

    function startHealthPolling(conn: ServerConnection.Any) {
      let alive = true
      let busy = false

      const run = () => {
        if (busy) return
        busy = true
        void check(conn)
          .then((next) => {
            if (!alive) return
            setState("healthy", next)
          })
          .finally(() => {
            busy = false
          })
      }

      run()
      const interval = setInterval(run, HEALTH_POLL_INTERVAL_MS)
      return () => {
        alive = false
        clearInterval(interval)
      }
    }

    function setActive(input: ServerConnection.Key) {
      if (state.active !== input) setState("active", input)
    }

    function add(input: ServerConnection.Http) {
      const url_ = normalizeServerUrl(input.http.url)
      if (!url_) return
      const conn = { ...input, http: { ...input.http, url: url_ } }
      return batch(() => {
        const list = storedList()
        const existing = list.findIndex((x) => storedServerUrl(x) === url_)
        setStore(
          "list",
          existing === -1 ? [...list, conn] : list.map((value, index) => (index === existing ? conn : value)),
        )
        setState("active", ServerConnection.key(conn))
        return conn
      })
    }

    function remove(key: ServerConnection.Key) {
      const list = storedList().filter((x) => storedServerUrl(x) !== key)
      batch(() => {
        setStore("list", list)
        if (state.active === key) {
          const next = list[0]
          setState("active", next ? ServerConnection.Key.make(storedServerUrl(next)) : props.defaultServer)
        }
      })
    }

    const isReady = createMemo(() => ready() && !!state.active)

    const check = (conn: ServerConnection.Any) => checkServerHealth(conn.http).then((x) => x.healthy)

    createEffect(() => {
      const current_ = current()
      if (!current_) return

      if (props.disableHealthCheck) {
        setState("healthy", true)
        return
      }
      setState("healthy", undefined)
      onCleanup(startHealthPolling(current_))
    })

    const scope = (key = state.active) => ServerScope.fromServerKey(key)
    const projects = createServerProjects({ scope: () => scope(), store, setStore })
    const projectStores = new Map<ServerConnection.Key, ReturnType<typeof createServerProjects>>()
    const projectsForServer = (key: ServerConnection.Key) => {
      const existing = projectStores.get(key)
      if (existing) return existing
      const next = createServerProjects({ scope: () => scope(key), store, setStore })
      projectStores.set(key, next)
      return next
    }
    const current: Accessor<ServerConnection.Any | undefined> = createMemo(() =>
      resolveCurrentServer(allServers(), state.active),
    )
    const isLocal = createMemo(() => ServerConnection.local(current()))

    return {
      ready: isReady,
      healthy,
      isLocal,
      get key() {
        return state.active
      },
      get name() {
        return serverName(current())
      },
      get list() {
        return allServers()
      },
      get current() {
        return current()
      },
      setActive,
      add,
      remove,
      scope,
      projects: {
        ...projects,
        forServer: projectsForServer,
      },
    }
  },
})
